import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import { authClient } from '../lib/auth-client.js';
import { describe as describeDevice } from './Sessions.js';
import { Note, Problem, button, input } from '../components/form.js';
import { VerifyFirst } from '../components/EmailVerification.js';
import { useEmailVerified } from '../lib/email-verification.js';

/**
 * The passkeys registered on this account (RidgeWill/account#15).
 *
 * ── ADDING ONE WORKS HERE NOW, AND DID NOT USED TO ──────────────────
 *
 * `rpID` was the auth service's hostname, so a credential was bound to
 * `auth-dev.ridgewill.com` and this portal — a SIBLING subdomain — could list
 * passkeys and never create one. RidgeWill/auth#156 moved the relying party to
 * the registrable domain, which is what makes the button below possible.
 *
 * The first version of this screen linked out to the hosted login instead. That
 * was honest about the limitation and a bad answer to it: "add one next time you
 * sign in" asks somebody to remember a task on a page they did not come here to
 * visit.
 *
 * ── THERE IS NO "LAST USED", AND THE ABSENCE IS THE HONEST ANSWER ────
 *
 * account#15 asked for it and the data does not exist: the plugin's `passkey`
 * model has `createdAt` and no last-used column, and nothing increments a
 * timestamp on authentication. Showing a fabricated or inferred value would be
 * worse than omitting it.
 *
 * What IS available and is arguably more useful: `backedUp` says whether the
 * credential is synced by the platform or password manager. That is the
 * difference between "losing this device loses this passkey" and "it does not",
 * which is the question somebody deciding whether they need a second one is
 * actually asking.
 */
type Passkey = {
  id: string;
  name?: string | null;
  createdAt: string | Date;
  deviceType?: string | null;
  backedUp?: boolean | null;
};

export function Passkeys({ email }: { email: string }) {
  /** Registration is refused until the address is proven (auth#587), so the
   *  control is replaced by the reason. Managing existing passkeys is not. */
  const verified = useEmailVerified();
  const [passkeys, setPasskeys] = useState<Passkey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  /** Tracked so the rename field can say WHY it opened when a passkey has just
   *  been registered. The same control, asking a different question. */
  const [justAdded, setJustAdded] = useState<string | null>(null);

  /**
   * `openNewest` puts the just-registered passkey straight into its rename
   * field, focused and pre-filled with the guessed name.
   *
   * Asking AFTER the credential exists rather than before it: the platform sheet
   * is the part that can fail or be dismissed, and a name typed into a form that
   * then never produces a passkey is wasted effort. This way the question is
   * only asked once there is something to answer it about.
   *
   * Newest by `createdAt` rather than by trusting the registration response to
   * carry an id. One field, read from the list that was just refreshed, so it
   * cannot disagree with what is on screen.
   */
  async function load(openNewest = false) {
    const { data, error: err } = await authClient.passkey.listUserPasskeys();
    if (err) {
      setError(err.message ?? 'Could not load your passkeys.');
      return;
    }
    setError(null);
    /**
     * `Array.isArray`, not a cast.
     *
     * `(data ?? []) as T[]` is a lie the compiler cannot check: a proxy error, an
     * HTML fallback or a changed response shape all arrive as SOMETHING, the cast
     * accepts it, and `.map` then throws — taking down the whole Security screen,
     * because these sections are now siblings on one page rather than separate
     * tabs. Consolidating raised the cost of this exact failure.
     *
     * Found by previewing: `TypeError: sessions.map is not a function`. Same
     * family as RidgeWill/account#16 and RidgeWill/auth#150 — a response that is
     * not what it claims, trusted because the type said so.
     */
    if (!Array.isArray(data)) {
      setError('Could not load your passkeys.');
      return;
    }
    const rows = data as Passkey[];
    setPasskeys(rows);

    if (openNewest && rows.length > 0) {
      const newest = [...rows].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0]!;
      setRenaming(newest.id);
      setDraft(newest.name?.trim() ?? '');
      setJustAdded(newest.id);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function remove(id: string) {
    setBusy(id);
    const { error: err } = await authClient.passkey.deletePasskey({ id });
    setBusy(null);
    if (err) {
      setError(err.message ?? 'That passkey could not be removed.');
      return;
    }
    await load();
  }

  async function rename(id: string) {
    const name = draft.trim();
    if (!name) return;
    setBusy(id);
    const { error: err } = await authClient.passkey.updatePasskey({ id, name });
    setBusy(null);
    setRenaming(null);
    setDraft('');
    setJustAdded(null);
    if (err) {
      setError(err.message ?? 'That passkey could not be renamed.');
      return;
    }
    await load();
  }

  if (error && !passkeys) return <Problem>{error}</Problem>;
  if (!passkeys) return <Note>Loading…</Note>;

  return (
    <>
      <Note>
        A passkey signs you in with your device&rsquo;s own unlock: a fingerprint, a face or a PIN.
        It replaces your password and cannot be phished.
      </Note>

      {error && <Problem>{error}</Problem>}

      {passkeys.length === 0 ? (
        <Note>You have no passkeys yet.</Note>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '1.5rem 0 0' }}>
          {passkeys.map((p) => (
            <li
              key={p.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                padding: '.85rem 0',
                borderTop: `1px solid ${C.inputBorder}`,
              }}
            >
              <div style={{ minWidth: 0 }}>
                {renaming === p.id ? (
                  <>
                    <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void rename(p.id);
                    }}
                    style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}
                  >
                    <input
                      style={{ ...input, marginBottom: 0 }}
                      value={draft}
                      autoFocus
                      maxLength={60}
                      aria-label="Passkey name"
                      onChange={(e) => setDraft(e.target.value)}
                    />
                    <button type="submit" style={button} disabled={busy === p.id}>
                      Save
                    </button>
                    <button
                      type="button"
                      style={{ ...button, borderColor: 'transparent' }}
                      onClick={() => {
                        setRenaming(null);
                        setDraft('');
                        setJustAdded(null);
                      }}
                    >
                      {/* "Keep" rather than "Cancel" for a passkey that was just
                          created: cancelling a rename discards an edit, but here
                          the credential is already saved and dismissing only
                          accepts the suggested name. "Cancel" would read as
                          undoing the registration. */}
                      {justAdded === p.id ? 'Keep' : 'Cancel'}
                    </button>
                  </form>
                    {justAdded === p.id && (
                      <Note>Name it so you can tell it apart from your other passkeys.</Note>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '.95rem' }}>{p.name?.trim() || 'Unnamed passkey'}</div>
                    <Note>
                      added {new Date(p.createdAt).toLocaleDateString()}
                      {/*
                        Spelled out rather than shown as a badge. "Synced" means
                        nothing to most people, and this is the line that tells
                        somebody whether losing the device loses the credential —
                        which is the whole reason to register a second one.
                      */}
                      {p.backedUp === true && ' · synced, so it survives losing this device'}
                      {p.backedUp === false && ' · on this device only'}
                    </Note>
                  </>
                )}
              </div>

              {renaming !== p.id && (
                <div style={{ display: 'flex', gap: '.5rem', flexShrink: 0 }}>
                  <button
                    type="button"
                    style={button}
                    onClick={() => {
                      setRenaming(p.id);
                      setDraft(p.name ?? '');
                    }}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    style={button}
                    disabled={busy === p.id}
                    onClick={() => {
                      /**
                       * Confirmed, and the copy does NOT promise a password.
                       *
                       * Every account has one today because sign-up requires it,
                       * so removing every passkey is currently recoverable. That
                       * stops being true the day passkey-first registration is
                       * enabled (RidgeWill/auth#147 closes on exactly that
                       * condition), and a message that said "you can still use
                       * your password" would then be a lie told at the moment it
                       * matters most.
                       */
                      if (
                        window.confirm(
                          `Remove ${p.name?.trim() || 'this passkey'}? You will not be able to sign in with it again.`,
                        )
                      ) {
                        void remove(p.id);
                      }
                    }}
                  >
                    {busy === p.id ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {verified ? (
        <AddPasskey accountName={email} onAdded={load} onError={setError} />
      ) : (
        <div style={{ marginTop: '1.5rem' }}>
          <VerifyFirst />
        </div>
      )}
    </>
  );
}

/**
 * Register a passkey for the signed-in account.
 *
 * ── THE BROWSER DOES THE WORK; THIS ONLY ASKS ───────────────────────
 *
 * `addPasskey()` calls `navigator.credentials.create()`, which is what raises
 * the platform's own sheet — Windows Hello, iCloud Keychain, or a password
 * manager like LastPass if it has registered itself as a provider. Which one
 * appears is the operating system's decision and not ours, so the copy does not
 * promise a particular chooser.
 *
 * ── THREE OUTCOMES, AND ONLY ONE IS A FAILURE ───────────────────────
 *
 * `InvalidStateError` means this authenticator ALREADY holds a credential for
 * this account. The browser reports it as an error and it is really a no-op —
 * saying "already registered" is both true and the only message that does not
 * send somebody hunting for a fault.
 *
 * `NotAllowedError` covers a dismissed prompt and a timeout, indistinguishably
 * and on purpose. Someone who changed their mind has not hit a problem, so it
 * renders as a status rather than in red.
 *
 * Anything else is real and says so.
 */
function AddPasskey({
  accountName,
  onAdded,
  onError,
}: {
  /** Names the CREDENTIAL, not the passkey. See the block comment in `add`. */
  accountName: string;
  onAdded: (newest: boolean) => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function add() {
    setBusy(true);
    setNote(null);
    onError(null);
    try {
      /**
       * `name` DOES TWO JOBS, AND THEY WANT DIFFERENT VALUES (#25).
       *
       * `addPasskey({ name })` sends that one string to two places:
       *
       *   1. as a QUERY PARAM to `/passkey/generate-register-options`, where the
       *      plugin uses it as `userName` in the WebAuthn creation options -
       *      `userName: ctx.query?.name || user.name || user.id`; and
       *   2. in the verify-registration body, where it becomes the passkey
       *      RECORD's label, which is what this list shows.
       *
       * Job 1 is the account identifier a password manager files the credential
       * under. This screen was passing a device description for job 2, so
       * LastPass stored the passkey under "Chrome on Windows" instead of under
       * the person - which is what #25 reports.
       *
       * So registration sends the ACCOUNT NAME, and the friendly label is
       * applied immediately afterwards. That order is deliberate: `userName` is
       * baked into the credential on the authenticator and cannot be corrected
       * later, while the record's label is editable from this very list. When
       * the two conflict, the unfixable one wins.
       */
      const result = await authClient.passkey.addPasskey({ name: accountName });
      if (result?.error) {
        setNote(result.error.message ?? 'That passkey could not be registered.');
        return;
      }

      /**
       * Now the readable label, so a user who registers and closes the tab is
       * left with something better than their own email repeated on every row.
       * The guess comes from the same helper the Devices list uses, so a passkey
       * and the session that created it describe the device the same way.
       *
       * NOT fatal if it fails. The credential exists and works; only its label
       * is wrong, and the row below opens for renaming anyway.
       */
      const created = result?.data as { id?: string } | undefined;
      if (created?.id) {
        await authClient.passkey.updatePasskey({
          id: created.id,
          name: describeDevice(navigator.userAgent),
        });
      }

      // Re-read rather than appending locally: the row carries `createdAt` and
      // `backedUp` from the server, and a hand-built entry would show neither
      // until the next load. `true` opens the new row for naming.
      await onAdded(true);
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      setNote(
        name === 'InvalidStateError'
          ? 'This device already has a passkey for this account.'
          : name === 'NotAllowedError'
            ? 'No passkey was created.'
            : 'This browser could not complete the passkey prompt.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <button type="button" style={button} onClick={add} disabled={busy}>
        {busy ? 'Waiting for your device…' : 'Add a passkey'}
      </button>
      {note && (
        <p role="status" style={{ marginTop: '.5rem', fontSize: '.9rem', color: C.textMid }}>
          {note}
        </p>
      )}
      <Note>
        A second passkey on another device is the simplest way to avoid being locked out.
      </Note>
    </div>
  );
}
